<template>
  <v-container fluid class="pa-4">
    <v-card outlined>
      <v-card-title class="text-h6 font-weight-bold">
        Calculadora de tiempo por TC
      </v-card-title>

      <v-card-text>
        <v-row dense>
          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="tcSegundos"
              label="TC (segundos por pieza)"
              type="number"
              min="0"
              step="0.0001"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="4">
            <v-text-field
              v-model.number="piezasEsperadas"
              label="Piezas esperadas"
              type="number"
              min="0"
              step="1"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="4" class="d-flex align-center">
            <v-btn text color="primary" @click="limpiarCampos">
              Limpiar
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="!canCalculate" dense outlined type="info" class="mt-3 mb-0">
          Ingresa TC y piezas esperadas mayores a 0 para calcular el tiempo de produccion.
        </v-alert>

        <v-row dense class="mt-3">
          <v-col cols="12" md="4">
            <v-card outlined class="resultado-card pa-3">
              <div class="text-caption grey--text text--darken-1">
                Tiempo total (segundos)
              </div>
              <div class="text-h6 font-weight-bold">
                {{ totalSegundosTexto }}
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="resultado-card pa-3">
              <div class="text-caption grey--text text--darken-1">
                Tiempo total (dias)
              </div>
              <div class="text-h6 font-weight-bold">
                {{ totalDiasTexto }}
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" md="4">
            <v-card outlined class="resultado-card pa-3">
              <div class="text-caption grey--text text--darken-1">
                Tiempo total (dias, horas, minutos, segundos)
              </div>
              <div class="text-h6 font-weight-bold">
                {{ tiempoDesglosado }}
              </div>
            </v-card>
          </v-col>
        </v-row>

        <v-divider class="my-6" />

        <div class="text-subtitle-1 font-weight-bold mb-2">
          Calculo inverso (obtener TC)
        </div>
        <div class="text-caption grey--text text--darken-1 mb-3">
          Ingresa piezas realizadas y el tiempo que tardaron para calcular el TC en segundos por pieza.
        </div>

        <v-row dense>
          <v-col cols="12" md="3">
            <v-text-field
              v-model.number="invPiezas"
              label="Piezas realizadas"
              type="number"
              min="0"
              step="1"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="invDias"
              label="Dias"
              type="number"
              min="0"
              step="1"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="invHoras"
              label="Horas"
              type="number"
              min="0"
              step="1"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="invMinutos"
              label="Minutos"
              type="number"
              min="0"
              step="1"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="2">
            <v-text-field
              v-model.number="invSegundos"
              label="Segundos"
              type="number"
              min="0"
              step="0.01"
              hide-details="auto"
            />
          </v-col>

          <v-col cols="12" md="1" class="d-flex align-center">
            <v-btn text color="primary" @click="limpiarInverso">
              Limpiar
            </v-btn>
          </v-col>
        </v-row>

        <v-alert v-if="!canCalculateInverse" dense outlined type="info" class="mt-3 mb-0">
          Ingresa piezas mayores a 0 y un tiempo mayor a 0 para obtener el TC.
        </v-alert>

        <v-row dense class="mt-3">
          <v-col cols="12" md="6">
            <v-card outlined class="resultado-card pa-3">
              <div class="text-caption grey--text text--darken-1">
                Tiempo capturado (segundos)
              </div>
              <div class="text-h6 font-weight-bold">
                {{ tiempoInversoSegundosTexto }}
              </div>
            </v-card>
          </v-col>
          <v-col cols="12" md="6">
            <v-card outlined class="resultado-card pa-3">
              <div class="text-caption grey--text text--darken-1">
                TC calculado (segundos por pieza)
              </div>
              <div class="text-h6 font-weight-bold">
                {{ tcInversoTexto }}
              </div>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
export default {
  name: "CalculadoraTc",

  data() {
    return {
      tcSegundos: null,
      piezasEsperadas: null,
      invPiezas: null,
      invDias: null,
      invHoras: null,
      invMinutos: null,
      invSegundos: null,
    };
  },

  computed: {
    tcValue() {
      return this.toNumber(this.tcSegundos);
    },

    piezasValue() {
      return this.toNumber(this.piezasEsperadas);
    },

    canCalculate() {
      return this.tcValue > 0 && this.piezasValue > 0;
    },

    totalSegundos() {
      if (!this.canCalculate) return 0;
      return this.tcValue * this.piezasValue;
    },

    totalDias() {
      if (!this.canCalculate) return 0;
      return this.totalSegundos / 86400;
    },

    totalSegundosTexto() {
      return this.formatNumber(this.totalSegundos, 2);
    },

    totalDiasTexto() {
      return this.formatNumber(this.totalDias, 6);
    },

    tiempoDesglosado() {
      if (!this.canCalculate) return "0 d 0 h 0 m 0 s";

      const dias = Math.floor(this.totalSegundos / 86400);
      let restante = this.totalSegundos - dias * 86400;

      const horas = Math.floor(restante / 3600);
      restante -= horas * 3600;

      const minutos = Math.floor(restante / 60);
      const segundos = restante - minutos * 60;

      return `${dias} d ${horas} h ${minutos} m ${this.formatNumber(segundos, 2)} s`;
    },

    inversePiezasValue() {
      return this.toNumber(this.invPiezas);
    },

    inverseDiasValue() {
      return this.toNumber(this.invDias);
    },

    inverseHorasValue() {
      return this.toNumber(this.invHoras);
    },

    inverseMinutosValue() {
      return this.toNumber(this.invMinutos);
    },

    inverseSegundosValue() {
      return this.toNumber(this.invSegundos);
    },

    inverseTotalSegundos() {
      return (
        this.inverseDiasValue * 86400 +
        this.inverseHorasValue * 3600 +
        this.inverseMinutosValue * 60 +
        this.inverseSegundosValue
      );
    },

    canCalculateInverse() {
      return this.inversePiezasValue > 0 && this.inverseTotalSegundos > 0;
    },

    tcInverso() {
      if (!this.canCalculateInverse) return 0;
      return this.inverseTotalSegundos / this.inversePiezasValue;
    },

    tiempoInversoSegundosTexto() {
      return this.formatNumber(this.inverseTotalSegundos, 2);
    },

    tcInversoTexto() {
      return this.formatNumber(this.tcInverso, 6);
    },
  },

  methods: {
    toNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },

    formatNumber(value, maxFractionDigits = 2) {
      return this.toNumber(value).toLocaleString("es-MX", {
        minimumFractionDigits: 0,
        maximumFractionDigits: maxFractionDigits,
      });
    },

    limpiarCampos() {
      this.tcSegundos = null;
      this.piezasEsperadas = null;
    },

    limpiarInverso() {
      this.invPiezas = null;
      this.invDias = null;
      this.invHoras = null;
      this.invMinutos = null;
      this.invSegundos = null;
    },
  },
};
</script>

<style scoped>
.resultado-card {
  min-height: 110px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
</style>
